import React, { useContext, useEffect, useRef } from 'react';
import useOnClickOutside from 'use-onclickoutside';
import { TouchScrollable } from 'react-scrolllock-configurable';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { initDone as initAppDoneSelector } from 'ggApp/modules/init/selectors';
import { newCheckoutVariants } from 'ggApp/modules/featureFlag/constants';
import {
    mixGlobalFlag as mixGlobalFlagSelector,
    newCheckoutFlagData as newCheckoutFlagDataSelector,
    newCheckoutEnabledSelector,
    cartOverlayVoucherPreviewEnabled as cartOverlayVoucherPreviewEnabledSelector,
    showVoucherExplanation as showVoucherExplanationSelector,
} from 'ggApp/modules/featureFlag/selectors';
import { Translator } from 'ggApp/modules/translator';
import { CURRENCIES_ASSOCIATION } from 'ggApp/modules/store/constants';
import flows from 'ggApp/modules/newCheckout/flows';
import { VocherExplenation } from 'ggApp/modules/newCheckout/components/VocherExplenation';

import { formatPriceByLanguage } from 'ggApp/utils/money';

import { OrderContext } from 'ggApp/shared/context/Order/OrderContext';
import { OrderModes } from 'ggApp/shared/context/Order/types';

import If from 'ggApp/shared/components/Conditional';
import CloseIcon from 'ggApp/shared/components/product/imageGallery/ZoomedGallery/CancelIcon';
import NewCheckoutModal from 'ggApp/shared/components/Cart/CartOverlay/NewCheckoutAuthGuard';
import { MixItemsContainer } from 'ggApp/shared/components/Cart/MixItemsContainer';
import { ItemCard } from 'ggApp/shared/components/Cart/ItemCard';
import { CART_CACHE_KEY } from 'ggApp/modules/tracking/constants';
import { VoucherPreview } from './VoucherPreview';

import {
    Wrap,
    LineItemsWrap,
    Empty,
    Summary,
    SummaryWrap,
    SummaryText,
    ANIMATION_DURATION_IN_MS,
    CloseButton,
    CloseButtonRow,
    MixContainer,
    Wrapper,
} from './styled';

interface ContentProps {
    className?: string;
    shouldShow?: boolean;
    mixGlobalFlag: boolean;
    companyTypeIsIndividual: boolean;
    newCheckoutFlagData: any;
    newCheckoutEnabled: boolean;
    clickOutside: () => void;
    cartOverlayVoucherPreviewEnabled: boolean;
    showVoucherExplanation: boolean;
    initAppDone: boolean;
}

export const Content = ({
    newCheckoutFlagData,
    className,
    clickOutside,
    shouldShow,
    newCheckoutEnabled,
    cartOverlayVoucherPreviewEnabled,
    showVoucherExplanation,
    initAppDone,
}: ContentProps): JSX.Element => {
    const { cart, activeStore, orderMode } = useContext(OrderContext);

    const ref = useRef(null);

    useOnClickOutside(ref, () => shouldShow && clickOutside());

    const checkoutFlow = () => {
        if (
            newCheckoutFlagData &&
            newCheckoutFlagData.variantKey === newCheckoutVariants.withHomeAddress
        ) {
            return flows.CHECKOUT_WITH_HOME_ADDRESS;
        }

        return flows.CHECKOUT_NO_HOME_ADDRESS;
    };

    const cartIsNotEmpty = cart?.items?.length ?? null;
    useEffect(() => {
        if (cart) {
            const items = cart?.items?.map(
                (cartItem: { variant: { sku: string }; priceCents: number; quantity: number }) => {
                    return {
                        itemId: cartItem.variant.sku,
                        unitPrice: Number((cartItem.priceCents / 100).toFixed(2)),
                        quantity: cartItem.quantity,
                    };
                },
            );
            const cartData = {
                cartSubtotal: Number((cart.itemTotal / 100).toFixed(2)),
                items,
            };
            localStorage.setItem(CART_CACHE_KEY, JSON.stringify(cartData));
        }
    }, [cart]);
    const showVoucherPreview = cartOverlayVoucherPreviewEnabled && orderMode === OrderModes.Flex;

    return (
        <Wrap ref={ref} className={className} id="cartOverlay" data-testid="cartoverlay-content">
            <Empty shouldShow={!cartIsNotEmpty} />

            <If condition={!!window.ReactNativeWebView}>
                <CloseButtonRow>
                    <CloseButton onClick={clickOutside}>
                        <CloseIcon />
                    </CloseButton>
                </CloseButtonRow>
            </If>

            {orderMode === OrderModes.Mix ? (
                <TouchScrollable>
                    <Wrapper data-testid="mix-line-items">
                        <MixContainer>
                            <MixItemsContainer
                                items={cart?.items ?? []}
                                closeOverlay={clickOutside}
                            />
                        </MixContainer>
                    </Wrapper>
                </TouchScrollable>
            ) : (
                <TouchScrollable>
                    <TransitionGroup component={LineItemsWrap} id="lineItemsWrap">
                        {(cart?.items ?? []).map((item) => (
                            <CSSTransition
                                key={item.id}
                                timeout={ANIMATION_DURATION_IN_MS}
                                classNames="cartItem"
                            >
                                <ItemCard item={item} />
                            </CSSTransition>
                        ))}
                    </TransitionGroup>
                </TouchScrollable>
            )}

            <Summary visible={cartIsNotEmpty} data-testid="cartoverlay-content-summary">
                {cartIsNotEmpty && showVoucherExplanation?.variantKey === 'cart_overlay' ? (
                    <VocherExplenation />
                ) : null}
                {showVoucherPreview && <VoucherPreview />}
                <SummaryWrap>
                    <SummaryText>
                        <Translator tk="CHECKOUT_ORDER_SUB_TOTAL" />
                        <Translator tk="NEW_CHECKOUT_SUMMARY_TOTAL_VAT_SUFFIX" />
                    </SummaryText>

                    <SummaryText>
                        {formatPriceByLanguage(
                            cart?.itemTotal ?? null,
                            CURRENCIES_ASSOCIATION[activeStore.default_currency],
                            activeStore.activeLanguage,
                        )}
                    </SummaryText>
                </SummaryWrap>

                {initAppDone && (
                    <NewCheckoutModal
                        checkoutFlow={checkoutFlow}
                        isLegacyCheckout={!newCheckoutEnabled}
                    />
                )}
            </Summary>
        </Wrap>
    );
};

const enhance = compose(
    connect(
        createStructuredSelector({
            newCheckoutFlagData: newCheckoutFlagDataSelector,
            newCheckoutEnabled: newCheckoutEnabledSelector,
            mixGlobalFlag: mixGlobalFlagSelector,
            cartOverlayVoucherPreviewEnabled: cartOverlayVoucherPreviewEnabledSelector,
            showVoucherExplanation: showVoucherExplanationSelector,
            initAppDone: initAppDoneSelector,
        }),
    ),
);

export default enhance(Content);
